import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    Promotion,
    CreatePromotionInput,
    UpdatePromotionInput,
    DeletePromotionInput,
    PromotionsByStatusQueryVariables,
    PromotionsByCreatedByQueryVariables,
    ModelPromotionFilterInput
} from '@app/core/services/api/API.service';

@Injectable({
    providedIn: 'root'
})
export class PromotionService {
    private amplifyClient = generateClient();

    async listPromotions(
        filter?: ModelPromotionFilterInput | null,
        limit = 500
    ): Promise<Promotion[]> {
        try {
            const items: Promotion[] = [];
            let nextToken: string | null | undefined = undefined;

            do {
                const response = await this.amplifyClient.graphql({
                    query: queries.listPromotions,
                    variables: {
                        filter: filter ?? undefined,
                        limit,
                        nextToken: nextToken ?? undefined
                    }
                });
                const page = response.data.listPromotions;
                if (page?.items?.length) {
                    items.push(...(page.items.filter(Boolean) as Promotion[]));
                }
                nextToken = page?.nextToken ?? null;
            } while (nextToken);

            return items;
        } catch (error) {
            console.error('Error listing promotions:', error);
            return null;
        }
    }

    /** Promociones activas aplicables al cliente (incluye promos globales sin clientes asignados). */
    listPromotionsForClient(clientId: string): Promise<Promotion[]> {
        const filter: ModelPromotionFilterInput = {
            and: [
                { status: { eq: 'active' } },
                {
                    or: [
                        { applicableClientIds: { contains: clientId } },
                        { applicableClientIds: { size: { eq: 0 } } },
                        { applicableClientIds: { attributeExists: false } }
                    ]
                }
            ]
        };
        return this.listPromotions(filter);
    }

    async getPromotion(id: string): Promise<Promotion> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getPromotion,
                    variables: { id }
                })
            ).data.getPromotion;
            if (data) return data as Promotion;
        } catch (error) {
            console.error('Error getting promotion:', error);
            return null;
        }
    }

    async createPromotion(input: CreatePromotionInput): Promise<Promotion> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createPromotion,
                    variables: { input }
                })
            ).data.createPromotion;
            if (data) return data as Promotion;
        } catch (error) {
            console.error('Error creating promotion:', error);
            return null;
        }
    }

    async updatePromotion(input: UpdatePromotionInput): Promise<Promotion> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePromotion,
                    variables: { input }
                })
            ).data.updatePromotion;
            if (data) return data as Promotion;
        } catch (error) {
            console.error('Error updating promotion:', error);
            return null;
        }
    }

    async deletePromotion(input: DeletePromotionInput): Promise<Promotion> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.deletePromotion,
                    variables: { input }
                })
            ).data.deletePromotion;
            if (data) return data as Promotion;
        } catch (error) {
            console.error('Error deleting promotion:', error);
            return null;
        }
    }

    async getPromotionsByStatus(
        status: string,
        variables?: Omit<PromotionsByStatusQueryVariables, 'status'>
    ): Promise<Promotion[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.promotionsByStatus,
                    variables: { status, ...variables }
                })
            ).data.promotionsByStatus.items;
            if (data) return data as Promotion[];
        } catch (error) {
            console.error('Error getting promotions by status:', error);
            return null;
        }
    }

    async getPromotionsByCreatedBy(
        createdBy: string,
        variables?: Omit<PromotionsByCreatedByQueryVariables, 'createdBy'>
    ): Promise<Promotion[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.promotionsByCreatedBy,
                    variables: { createdBy, ...variables }
                })
            ).data.promotionsByCreatedBy.items;
            if (data) return data as Promotion[];
        } catch (error) {
            console.error('Error getting promotions by createdBy:', error);
            return null;
        }
    }
}
